/**
 * What one unit of an item nets on each channel of the window.
 *
 * `/analytics` used to send these rows on every item it returned. They hold
 * nothing the dashboard does not already carry — each one is this item's own
 * price and cost against a commission listed in `channels` — and at a 30k-item
 * catalog they measured 24 MB of a 35 MB response, so the server stopped
 * building them and sends `operatingCostPct` instead. They are worked out here
 * for the item the owner has open.
 *
 * The figures have to match `netProfit` on the same card to the cent, so this
 * reproduces the engine exactly: cost from the item's own inputs rather than
 * its rounded `fcd`, and the operating percentage unrounded as it arrives.
 *
 * Each channel is priced at its own price, which the server resolved into
 * `channelPrices`: MMS marks a product up per provider to cover that provider's
 * commission, so one price across the rows understated every delivery channel by
 * the whole markup.
 */
export function itemChannelRows(item, channels, operatingCostPct) {
  // A stock-tracked item costs what it was bought for; everything else is
  // charged a share of the basis, which is the menu price plus the modifier
  // options ordered. The same food whichever channel sold it, so this is not
  // charged against the channel's own price.
  const fcd =
    item.costDollar != null
      ? item.costDollar
      : item.costBasis * (item.foodCostPct / 100);

  return channels.map((ch) => {
    const price = item.channelPrices[ch.channelId];
    // The price has this channel's commission taken off it, so the operating
    // percentage charged here is fee-free. It is charged on the full price,
    // which is what makes this row equal the item's net profit when the item
    // sold on this channel alone.
    const netPrice = price * (1 - ch.effCommission / 100);
    const profit = netPrice - fcd - (price * operatingCostPct) / 100;
    return {
      channelId: ch.channelId,
      name: ch.name,
      color: ch.color,
      commission: ch.effCommission,
      price,
      netPrice: round2(netPrice),
      profit: round2(profit),
      profitPct: price > 0 ? (profit / price) * 100 : 0,
    };
  });
}

function round2(n) {
  return Math.round(n * 100) / 100;
}
